import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import {
  fetchGovernedLibraryDocuments,
  type GovernedLibraryDocumentSummary,
} from "../api/cognitoryLibraryClient";
import { uploadGovernedLibraryPdf } from "../api/cognitoryLibraryUploadClient";
import {
  fetchGovernedLibrarySource,
  openGovernedLibrarySourceBlob,
} from "../api/cognitoryLibrarySourceClient";
import { CognitoryLibraryDeleteDialog } from "./CognitoryLibraryDeleteDialog";
import { CognitoryLibraryDocumentRow } from "./CognitoryLibraryDocumentRow";
import { CognitoryLibraryToast } from "./CognitoryLibraryToast";
import { CognitoryLibraryUploadStatusPanel } from "./CognitoryLibraryUploadStatusPanel";
import {
  runGovernedLibraryDeleteFlow,
  GOVERNED_LIBRARY_FINAL_DOCUMENT_MESSAGE,
  isTerminalDeleteSuccess,
} from "./cognitoryLibraryDeleteFlow";
import { fetchGovernedLibraryRemovalStatus } from "../api/cognitoryLibraryDeleteClient";
import { pollRemovalUntilTerminal } from "./cognitoryLibraryDeletePolling";
import {
  excludeDocumentFromLibraryList,
  mergeLibraryDocumentList,
  shouldApplyLibraryFetchResult,
  upsertLibraryDocument,
} from "./cognitoryLibraryListState";
import { buildUploadPlaceholderDocument } from "./cognitoryLibraryUploadPlaceholder";
import { pollUploadIngestionUntilTerminal } from "./cognitoryLibraryUploadPolling";
import {
  createUploadAttemptLifecycle,
  type UploadAttemptLifecycle,
} from "./cognitoryLibraryUploadLifecycle";
import {
  isUploadProgressTerminal,
  resolveUploadFailureMessage,
  resolveUploadProgressStage,
} from "./cognitoryLibraryUploadStage";
import {
  MAIN_PAGE_BODY_STYLE,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_SECTION_LABEL_STYLE,
} from "../theme/mainPageVisual";
import "./CognitoryLibraryShell.css";

export { resolveLibraryItemUserVisibleLabel } from "./cognitoryLibraryIngestionStatus";

type ActiveUploadSession = {
  filename: string;
  documentId: string | null;
  documentVersionId: string | null;
  httpUploading: boolean;
};

type LibraryToastState = {
  message: string;
  secondaryMessage?: string | null;
  variant: "success" | "error";
} | null;

export default function CognitoryLibraryShell(input: { cognitoryId: string }) {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<GovernedLibraryDocumentSummary[]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(
    null,
  );
  const [openError, setOpenError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<GovernedLibraryDocumentSummary | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deletePhase, setDeletePhase] = useState<
    "idle" | "deleting" | "rebuilding_library" | "removed" | "failed"
  >("idle");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [uploadTimedOutDocumentIds, setUploadTimedOutDocumentIds] = useState<
    string[]
  >([]);
  const [activeUploadSession, setActiveUploadSession] =
    useState<ActiveUploadSession | null>(null);
  const [toast, setToast] = useState<LibraryToastState>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const libraryFetchGenerationRef = useRef(0);
  const deleteAbortRef = useRef<AbortController | null>(null);
  const deleteCompletionRef = useRef<string | null>(null);
  const uploadPollingAbortRef = useRef<AbortController | null>(null);
  const trackedUploadDocumentIdsRef = useRef<Set<string>>(new Set());
  const trackedUploadLifecyclesRef = useRef<
    Map<string, UploadAttemptLifecycle>
  >(new Map());
  const excludedDocumentIdsRef = useRef<Set<string>>(new Set());
  const readyNotifiedDocumentIdsRef = useRef<Set<string>>(new Set());
  const backgroundDeleteAbortRef = useRef<AbortController | null>(null);

  const showToast = useCallback((next: Exclude<LibraryToastState, null>) => {
    setToast(next);
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  const maybeNotifyUploadReady = useCallback(
    (listed: GovernedLibraryDocumentSummary[]) => {
      for (const documentId of trackedUploadDocumentIdsRef.current) {
        if (readyNotifiedDocumentIdsRef.current.has(documentId)) {
          continue;
        }
        const document = listed.find(entry => entry.documentId === documentId);
        if (!document) {
          continue;
        }
        const stage = resolveUploadProgressStage({
          httpUploading: false,
          document,
          uploadLifecycle: trackedUploadLifecyclesRef.current.get(documentId),
        });
        if (stage !== "ready") {
          continue;
        }
        readyNotifiedDocumentIdsRef.current.add(documentId);
        showToast({
          message: `${document.title} is ready.`,
          secondaryMessage:
            "You can now ask Cognitory questions grounded in this document.",
          variant: "success",
        });
      }
    },
    [showToast],
  );

  const applyListedDocuments = useCallback(
    (
      incoming: GovernedLibraryDocumentSummary[],
      options?: { replace?: boolean; authoritative?: boolean },
    ) => {
      const monotonicDocumentIds = options?.authoritative
        ? new Set<string>()
        : trackedUploadDocumentIdsRef.current;
      setDocuments(current => {
        if (options?.replace) {
          return mergeLibraryDocumentList({
            current: [],
            incoming,
            excludedDocumentIds: excludedDocumentIdsRef.current,
            monotonicDocumentIds,
          });
        }
        return mergeLibraryDocumentList({
          current,
          incoming,
          excludedDocumentIds: excludedDocumentIdsRef.current,
          monotonicDocumentIds,
        });
      });
    },
    [],
  );

  const fetchLibraryList = useCallback(
    async (options?: { generation?: number }) => {
      const generation =
        options?.generation ?? libraryFetchGenerationRef.current + 1;
      if (!options?.generation) {
        libraryFetchGenerationRef.current = generation;
      }

      const result = await fetchGovernedLibraryDocuments({
        cognitoryId: input.cognitoryId,
      });

      if (
        !shouldApplyLibraryFetchResult({
          requestGeneration: generation,
          activeGeneration: libraryFetchGenerationRef.current,
        })
      ) {
        return null;
      }

      if (result.kind === "listed") {
        return result.documents;
      }
      return result;
    },
    [input.cognitoryId],
  );

  const softRefetchLibrary = useCallback(async () => {
    const generation = libraryFetchGenerationRef.current + 1;
    libraryFetchGenerationRef.current = generation;
    const listed = await fetchLibraryList({ generation });
    if (Array.isArray(listed)) {
      applyListedDocuments(listed);
      return listed;
    }
    return null;
  }, [applyListedDocuments, fetchLibraryList]);

  const startUploadConvergencePolling = useCallback(() => {
    if (trackedUploadDocumentIdsRef.current.size === 0) {
      return;
    }

    uploadPollingAbortRef.current?.abort();
    const abortController = new AbortController();
    uploadPollingAbortRef.current = abortController;

    void pollUploadIngestionUntilTerminal({
      trackedDocumentIds: trackedUploadDocumentIdsRef.current,
      trackedUploadLifecycles: trackedUploadLifecyclesRef.current,
      signal: abortController.signal,
      fetchDocuments: async () => {
        const listed = await softRefetchLibrary();
        return listed ?? [];
      },
      sleep: ms =>
        new Promise(resolve => {
          window.setTimeout(resolve, ms);
        }),
      onDocuments: listed => {
        if (abortController.signal.aborted) {
          return;
        }
        applyListedDocuments(listed);
        maybeNotifyUploadReady(listed);
      },
    }).then(async result => {
      if (abortController.signal.aborted) {
        return;
      }

      const trackedIds = [...trackedUploadDocumentIdsRef.current];
      const generation = libraryFetchGenerationRef.current + 1;
      libraryFetchGenerationRef.current = generation;
      const listed = await fetchLibraryList({ generation });
      if (Array.isArray(listed)) {
        applyListedDocuments(listed, { authoritative: true });
        maybeNotifyUploadReady(listed);
      }

      uploadPollingAbortRef.current = null;

      for (const documentId of trackedIds) {
        trackedUploadDocumentIdsRef.current.delete(documentId);
        trackedUploadLifecyclesRef.current.delete(documentId);
      }

      if (result.kind === "timeout") {
        setUploadTimedOutDocumentIds(current => {
          const merged = new Set(current);
          for (const documentId of result.timedOutDocumentIds) {
            merged.add(documentId);
          }
          return [...merged];
        });
      }

      setActiveUploadSession(current => {
        if (!current?.documentId) {
          return current;
        }
        const tracked = result.kind === "timeout";
        if (!tracked && trackedUploadDocumentIdsRef.current.size === 0) {
          return null;
        }
        return current;
      });
    });
  }, [
    applyListedDocuments,
    fetchLibraryList,
    maybeNotifyUploadReady,
    softRefetchLibrary,
  ]);

  const refreshLibrary = useCallback(
    (options?: { excludeDocumentId?: string }) => {
      const generation = libraryFetchGenerationRef.current + 1;
      libraryFetchGenerationRef.current = generation;
      setLoading(true);
      setError(null);
      if (options?.excludeDocumentId) {
        excludedDocumentIdsRef.current.add(options.excludeDocumentId);
        trackedUploadDocumentIdsRef.current.delete(options.excludeDocumentId);
        trackedUploadLifecyclesRef.current.delete(options.excludeDocumentId);
        setDocuments(current =>
          excludeDocumentFromLibraryList({
            documents: current,
            documentId: options.excludeDocumentId!,
          }),
        );
      }

      void fetchGovernedLibraryDocuments({
        cognitoryId: input.cognitoryId,
      }).then(result => {
        if (
          !shouldApplyLibraryFetchResult({
            requestGeneration: generation,
            activeGeneration: libraryFetchGenerationRef.current,
          })
        ) {
          return;
        }
        setLoading(false);
        if (result.kind === "listed") {
          applyListedDocuments(result.documents, { replace: true });
          return;
        }
        if (result.kind === "unauthenticated") {
          setError("Sign in again to view your library.");
          return;
        }
        if (result.kind === "forbidden") {
          setError("You do not have access to this library.");
          return;
        }
        setError("Unable to load library documents right now.");
      });
    },
    [applyListedDocuments, input.cognitoryId],
  );

  const finalizeDeleteSuccess = useCallback(
    (documentId: string, options?: { title?: string }) => {
      if (deleteCompletionRef.current === documentId) {
        return;
      }
      deleteCompletionRef.current = documentId;
      deleteAbortRef.current = null;
      setDeleting(false);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      setDeletePhase("idle");
      setDeleteError(null);
      if (options?.title) {
        showToast({
          message: `${options.title} was deleted.`,
          variant: "success",
        });
      }
      refreshLibrary({ excludeDocumentId: documentId });
    },
    [refreshLibrary, showToast],
  );

  const startBackgroundDeletePolling = useCallback(
    (deleteInput: { documentId: string; title: string }) => {
      backgroundDeleteAbortRef.current?.abort();
      const abortController = new AbortController();
      backgroundDeleteAbortRef.current = abortController;

      void pollRemovalUntilTerminal({
        cognitoryId: input.cognitoryId,
        documentId: deleteInput.documentId,
        fetchStatus: fetchGovernedLibraryRemovalStatus,
        signal: abortController.signal,
        sleep: ms =>
          new Promise(resolve => {
            window.setTimeout(resolve, ms);
          }),
        onPhaseChange: () => {
          void softRefetchLibrary();
        },
      }).then(result => {
        if (abortController.signal.aborted) {
          return;
        }
        backgroundDeleteAbortRef.current = null;

        if (result.kind === "removed") {
          finalizeDeleteSuccess(deleteInput.documentId, {
            title: deleteInput.title,
          });
          return;
        }

        if (result.kind === "failed") {
          showToast({
            message: "Cognitory couldn't complete the deletion yet.",
            secondaryMessage: "Try again in a moment.",
            variant: "error",
          });
          void softRefetchLibrary();
          return;
        }

        if (result.kind === "still_processing") {
          showToast({
            message: "Cognitory is still removing this document.",
            secondaryMessage:
              "The library will update automatically when deletion finishes.",
            variant: "success",
          });
          void softRefetchLibrary();
        }
      });
    },
    [finalizeDeleteSuccess, input.cognitoryId, showToast, softRefetchLibrary],
  );

  useEffect(() => {
    let cancelled = false;
    const generation = libraryFetchGenerationRef.current + 1;
    libraryFetchGenerationRef.current = generation;
    setLoading(true);
    setError(null);

    void fetchGovernedLibraryDocuments({ cognitoryId: input.cognitoryId }).then(
      result => {
        if (cancelled) {
          return;
        }
        if (
          !shouldApplyLibraryFetchResult({
            requestGeneration: generation,
            activeGeneration: libraryFetchGenerationRef.current,
          })
        ) {
          return;
        }
        setLoading(false);
        if (result.kind === "listed") {
          applyListedDocuments(result.documents, { replace: true });
          return;
        }
        if (result.kind === "unauthenticated") {
          setError("Sign in again to view your library.");
          return;
        }
        if (result.kind === "forbidden") {
          setError("You do not have access to this library.");
          return;
        }
        setError("Unable to load library documents right now.");
      },
    );

    return () => {
      cancelled = true;
      uploadPollingAbortRef.current?.abort();
      uploadPollingAbortRef.current = null;
      backgroundDeleteAbortRef.current?.abort();
      backgroundDeleteAbortRef.current = null;
    };
  }, [applyListedDocuments, input.cognitoryId]);

  const onUploadClick = useCallback(() => {
    uploadInputRef.current?.click();
  }, []);

  const onUploadSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || uploading) {
        return;
      }

      setUploading(true);
      setUploadError(null);
      setActiveUploadSession({
        filename: file.name,
        documentId: null,
        documentVersionId: null,
        httpUploading: true,
      });

      const result = await uploadGovernedLibraryPdf({
        cognitoryId: input.cognitoryId,
        file,
      });

      setUploading(false);

      if (result.kind === "uploaded") {
        const uploadLifecycle = createUploadAttemptLifecycle({
          documentId: result.documentId,
          documentVersionId: result.documentVersionId,
        });
        trackedUploadDocumentIdsRef.current.add(result.documentId);
        trackedUploadLifecyclesRef.current.set(
          result.documentId,
          uploadLifecycle,
        );
        setUploadTimedOutDocumentIds(current =>
          current.filter(documentId => documentId !== result.documentId),
        );
        setActiveUploadSession({
          filename: file.name,
          documentId: result.documentId,
          documentVersionId: result.documentVersionId,
          httpUploading: false,
        });
        const placeholder = buildUploadPlaceholderDocument(result);
        const generation = libraryFetchGenerationRef.current + 1;
        libraryFetchGenerationRef.current = generation;
        const listed = await fetchLibraryList({ generation });
        setDocuments(current => {
          const seeded = upsertLibraryDocument({
            documents: current,
            document: placeholder,
          });
          if (!Array.isArray(listed)) {
            return seeded;
          }
          return mergeLibraryDocumentList({
            current: seeded,
            incoming: listed,
            excludedDocumentIds: excludedDocumentIdsRef.current,
            monotonicDocumentIds: trackedUploadDocumentIdsRef.current,
          });
        });
        startUploadConvergencePolling();
        return;
      }

      setActiveUploadSession(null);

      if (result.kind === "forbidden") {
        setUploadError(
          resolveUploadFailureMessage({
            filename: file.name,
            kind: "forbidden",
          }),
        );
        return;
      }
      if (result.kind === "pdf_rejected") {
        setUploadError(
          resolveUploadFailureMessage({
            filename: file.name,
            kind: "pdf_rejected",
          }),
        );
        return;
      }
      if (result.kind === "document_collision") {
        setUploadError(
          resolveUploadFailureMessage({
            filename: file.name,
            kind: "document_collision",
          }),
        );
        return;
      }
      setUploadError(
        resolveUploadFailureMessage({ filename: file.name, kind: "unknown" }),
      );
    },
    [
      fetchLibraryList,
      input.cognitoryId,
      startUploadConvergencePolling,
      uploading,
    ],
  );

  const openDocument = useCallback(
    async (document: GovernedLibraryDocumentSummary) => {
      if (!document.governedSourceRecorded || openingDocumentId) {
        return;
      }

      setOpeningDocumentId(document.documentId);
      setOpenError(null);

      const result = await fetchGovernedLibrarySource({
        cognitoryId: input.cognitoryId,
        documentId: document.documentId,
      });

      setOpeningDocumentId(null);

      if (result.kind === "opened") {
        openGovernedLibrarySourceBlob({
          blob: result.blob,
          contentType: result.contentType,
        });
        return;
      }

      if (result.kind === "not_available") {
        setOpenError("This document source is not available.");
        return;
      }
      if (result.kind === "forbidden") {
        setOpenError("You do not have access to this document source.");
        return;
      }
      setOpenError("Unable to open this document source right now.");
    },
    [input.cognitoryId, openingDocumentId],
  );

  const onDeleteClick = useCallback(
    (
      document: GovernedLibraryDocumentSummary,
      trigger: HTMLButtonElement | null,
    ) => {
      deleteAbortRef.current?.abort();
      deleteAbortRef.current = null;
      deleteCompletionRef.current = null;
      deleteTriggerRef.current = trigger;
      setDeleteTarget(document);
      setDeleteConfirmText("");
      setDeleteError(null);
      setDeletePhase("idle");
      setDeleting(false);
    },
    [],
  );

  const onDeleteCancel = useCallback(() => {
    deleteAbortRef.current?.abort();
    deleteAbortRef.current = null;
    setDeleting(false);
    setDeleteTarget(null);
    setDeleteConfirmText("");
    setDeleteError(null);
    setDeletePhase("idle");
  }, []);

  const onDeleteConfirm = useCallback(async () => {
    if (!deleteTarget || deleting) {
      return;
    }
    if (deleteConfirmText.trim() !== deleteTarget.documentId) {
      setDeleteError("Type the document ID exactly to confirm deletion.");
      return;
    }

    deleteAbortRef.current?.abort();
    const abortController = new AbortController();
    deleteAbortRef.current = abortController;
    const documentId = deleteTarget.documentId;

    setDeleting(true);
    setDeleteError(null);
    setDeletePhase("deleting");

    const result = await runGovernedLibraryDeleteFlow({
      cognitoryId: input.cognitoryId,
      documentId,
      documentTitle: deleteTarget.title,
      confirmed: true,
      signal: abortController.signal,
      onPhaseChange: phase => {
        if (abortController.signal.aborted) {
          return;
        }
        setDeletePhase(phase);
      },
    });

    if (abortController.signal.aborted || result.kind === "aborted") {
      return;
    }

    if (isTerminalDeleteSuccess(result)) {
      finalizeDeleteSuccess(documentId, { title: deleteTarget.title });
      return;
    }

    setDeleting(false);

    if (result.kind === "blocked_final_library_document") {
      setDeleteError(result.message || GOVERNED_LIBRARY_FINAL_DOCUMENT_MESSAGE);
      setDeletePhase("failed");
      return;
    }

    if (result.kind === "still_processing") {
      setDeleteTarget(null);
      setDeleteConfirmText("");
      setDeletePhase("idle");
      setDeleteError(null);
      showToast({
        message: "Cognitory is still removing this document.",
        secondaryMessage:
          "The library will update automatically when deletion finishes.",
        variant: "success",
      });
      startBackgroundDeletePolling({
        documentId,
        title: deleteTarget.title,
      });
      void softRefetchLibrary();
      return;
    }

    if (result.kind === "error") {
      setDeletePhase("failed");
      setDeleteError(
        result.message === "Document removal did not complete successfully."
          ? "Cognitory couldn't complete the deletion yet."
          : result.message,
      );
    }
  }, [
    deleteConfirmText,
    deleteTarget,
    deleting,
    finalizeDeleteSuccess,
    input.cognitoryId,
    softRefetchLibrary,
    startBackgroundDeletePolling,
    showToast,
  ]);

  const modalBlocksLibrary = Boolean(deleteTarget);
  const uploadTimedOutSet = new Set(uploadTimedOutDocumentIds);
  const activeUploadDocument = activeUploadSession?.documentId
    ? documents.find(
        document => document.documentId === activeUploadSession.documentId,
      )
    : null;
  const activeUploadTimedOut = activeUploadSession?.documentId
    ? uploadTimedOutSet.has(activeUploadSession.documentId)
    : false;
  const showUploadStatusPanel = Boolean(activeUploadSession);

  const activeUploadLifecycle =
    activeUploadSession?.documentId != null
      ? (trackedUploadLifecyclesRef.current.get(
          activeUploadSession.documentId,
        ) ?? null)
      : null;

  useEffect(() => {
    if (!activeUploadSession?.documentId || activeUploadSession.httpUploading) {
      return;
    }
    const stage = resolveUploadProgressStage({
      httpUploading: false,
      document: activeUploadDocument ?? null,
      uploadLifecycle: activeUploadLifecycle,
    });
    if (isUploadProgressTerminal(stage)) {
      const timer = window.setTimeout(() => {
        setActiveUploadSession(current => {
          if (current?.documentId !== activeUploadSession.documentId) {
            return current;
          }
          return null;
        });
      }, 4000);
      return () => {
        window.clearTimeout(timer);
      };
    }
    return undefined;
  }, [activeUploadDocument, activeUploadLifecycle, activeUploadSession]);

  return (
    <div
      data-testid="cognitory-library-shell"
      data-cognitory-visual-shell="main-page"
      data-library-modal-open={deleteTarget ? "true" : "false"}
      className={`cognitory-library-shell${deleteTarget ? " cognitory-library-shell--modal-open" : ""}`}
      style={{
        ...MAIN_PAGE_BODY_STYLE,
        color: MAIN_PAGE_COLORS.slate,
      }}
    >
      <p style={MAIN_PAGE_SECTION_LABEL_STYLE}>Governed documents</p>

      <div className="cognitory-library-upload-row">
        <input
          ref={uploadInputRef}
          type="file"
          data-testid="cognitory-library-upload-input"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          onChange={event => void onUploadSelected(event)}
          disabled={uploading || modalBlocksLibrary}
          aria-label="Upload governed PDF"
        />
        <button
          type="button"
          data-testid="cognitory-library-upload"
          style={{
            ...MAIN_PAGE_QUIET_BUTTON_STYLE,
            marginTop: 0,
            opacity: uploading ? 0.6 : 1,
            cursor: uploading ? "not-allowed" : "pointer",
          }}
          disabled={uploading || modalBlocksLibrary}
          onClick={onUploadClick}
        >
          {uploading ? "Uploading…" : "Upload PDF"}
        </button>
      </div>

      {showUploadStatusPanel && activeUploadSession ? (
        <CognitoryLibraryUploadStatusPanel
          filename={activeUploadSession.filename}
          httpUploading={activeUploadSession.httpUploading}
          document={activeUploadDocument ?? null}
          timedOut={activeUploadTimedOut}
          {...(activeUploadLifecycle
            ? { uploadLifecycle: activeUploadLifecycle }
            : {})}
        />
      ) : null}

      {uploadError ? (
        <p
          data-testid="cognitory-library-upload-error"
          style={{ color: MAIN_PAGE_COLORS.soft, marginBottom: "0.75rem" }}
        >
          {uploadError}
        </p>
      ) : null}

      {loading ? (
        <p data-testid="cognitory-library-loading">Loading library…</p>
      ) : null}

      {error ? (
        <p
          data-testid="cognitory-library-error"
          style={{ color: MAIN_PAGE_COLORS.soft }}
        >
          {error}
        </p>
      ) : null}

      {!loading && !error && documents.length === 0 ? (
        <p data-testid="cognitory-library-empty">No governed documents yet.</p>
      ) : null}

      {!loading && !error && documents.length > 0 ? (
        <ul
          data-testid="cognitory-library-list"
          className="cognitory-library-list"
        >
          {documents.map(document => (
            <CognitoryLibraryDocumentRow
              key={document.documentId}
              document={document}
              timedOut={uploadTimedOutSet.has(document.documentId)}
              modalBlocksLibrary={modalBlocksLibrary}
              openingDocumentId={openingDocumentId}
              onView={openDocument}
              onDelete={onDeleteClick}
            />
          ))}
        </ul>
      ) : null}

      {openError ? (
        <p
          data-testid="cognitory-library-open-error"
          style={{ marginTop: "0.85rem", color: MAIN_PAGE_COLORS.soft }}
        >
          {openError}
        </p>
      ) : null}

      {deleteTarget ? (
        <CognitoryLibraryDeleteDialog
          target={deleteTarget}
          confirmText={deleteConfirmText}
          deleting={deleting}
          deletePhase={deletePhase}
          error={deleteError}
          onConfirmTextChange={setDeleteConfirmText}
          onCancel={onDeleteCancel}
          onConfirm={() => void onDeleteConfirm()}
          returnFocusRef={deleteTriggerRef}
        />
      ) : null}

      {toast ? (
        <CognitoryLibraryToast
          message={toast.message}
          {...(toast.secondaryMessage != null
            ? { secondaryMessage: toast.secondaryMessage }
            : {})}
          variant={toast.variant}
          onDismiss={dismissToast}
        />
      ) : null}
    </div>
  );
}
